import { useCallback, useEffect, useState } from 'react';
import { CloudUpload, Search } from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { mediaQueueRepo } from '@/data/repositories/mediaQueueRepo';
import { logQueueRepo } from '@/data/repositories/logQueueRepo';
import { mediaQueueService } from '@/services/mediaQueue';
import { logQueueService } from '@/services/logQueue';
import { showToast } from '@/components/ui/Toast';
import { PullToRefresh } from '@/components/PullToRefresh';
import { isVerifiableField, isSignatureMedia, type MediaQueueItem } from '@/types/domain';

const STALE_MS = 6 * 60 * 60 * 1000;

function statusOf(m: MediaQueueItem): { text: string; tone: string } {
  if (m.status === 'pending' || m.status === 'inflight') {
    return { text: 'PENDING', tone: 'bg-brand-warn/15 text-brand-warn' };
  }
  if (m.status === 'quarantined') {
    return { text: 'FAILED', tone: 'bg-brand-error/15 text-brand-error' };
  }
  // uploaded
  if (isVerifiableField(m.fieldName)) {
    if (m.verified) return { text: 'VERIFIED', tone: 'bg-brand-delivery/15 text-brand-delivery' };
    const stamp = m.uploadedAt ?? m.capturedAt;
    const stale = Date.now() - Date.parse(stamp) > STALE_MS;
    return stale
      ? { text: 'UNVERIFIED', tone: 'bg-brand-error/15 text-brand-error' }
      : { text: 'UPLOADED', tone: 'bg-brand-primaryBlue/15 text-brand-primaryBlue' };
  }
  return { text: 'UPLOADED', tone: 'bg-brand-delivery/15 text-brand-delivery' };
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${two(d.getHours())}:${two(
    d.getMinutes(),
  )}`;
}

export function PodRecoveryPage() {
  const [pro, setPro] = useState('');
  const [signaturesOnly, setSignaturesOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<MediaQueueItem[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [staleCount, setStaleCount] = useState(0);
  const [quarantinedCount, setQuarantinedCount] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [found, pending, stale, quarantined] = await Promise.all([
      mediaQueueRepo.findRetained({ tag: pro, signaturesOnly }),
      mediaQueueRepo.pendingCount(),
      mediaQueueRepo.staleUnverifiedCount(),
      logQueueRepo.getQuarantinedCount(),
    ]);
    setItems(found);
    setPendingCount(pending);
    setStaleCount(stale);
    setQuarantinedCount(quarantined);
    setLoading(false);
  }, [pro, signaturesOnly]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const resend = async (m: MediaQueueItem) => {
    setBusy(true);
    try {
      await mediaQueueRepo.requeueForResend(m.id);
      await mediaQueueService.flush();
      showToast(`Queued ${labelOf(m)} for re-upload`);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const resendAllUploaded = async () => {
    const uploaded = items.filter((i) => i.status === 'uploaded');
    if (uploaded.length === 0) {
      showToast('No confirmed media to re-send');
      return;
    }
    setBusy(true);
    try {
      for (const m of uploaded) await mediaQueueRepo.requeueForResend(m.id);
      await mediaQueueService.flush();
      showToast(`Queued ${uploaded.length} item(s) for re-upload`);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const resetFailedLogs = async () => {
    setBusy(true);
    try {
      await logQueueRepo.resetQuarantined();
      await logQueueService.flush();
      showToast(quarantinedCount > 0 ? `Reset ${quarantinedCount} failed log(s) — re-pushing` : 'No failed logs');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="POD Recovery" showBack>
      <div className="rounded bg-black/40 px-4 py-3">
        <p className="text-xs text-brand-muted">
          {items.length} retained • {pendingCount} awaiting upload • {staleCount} unverified &gt;6h •{' '}
          {quarantinedCount} failed log(s)
        </p>
        <p className="mt-1 text-[11px] text-brand-muted/70">
          Legal PODs are kept until verified on the server, then 30 days; photos 30 days after upload.
        </p>
        {quarantinedCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="mt-2 w-full text-brand-warn"
            disabled={busy}
            onClick={resetFailedLogs}
          >
            Reset {quarantinedCount} failed log(s) &amp; retry
          </Button>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-muted" />
          <Input
            aria-label="Filter by PRO"
            placeholder="Filter by PRO"
            value={pro}
            onChange={(e) => setPro(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant={signaturesOnly ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setSignaturesOnly((v) => !v)}
        >
          PODs only
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Re-send all confirmed"
          disabled={busy}
          onClick={resendAllUploaded}
        >
          <CloudUpload className="h-4 w-4" />
        </Button>
      </div>

      <PullToRefresh onRefresh={refresh}>
        {loading ? (
          <p className="mt-4 text-brand-muted">Loading...</p>
        ) : items.length === 0 ? (
          <p className="mt-4 text-center text-brand-muted">No retained media</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {items.map((m) => {
              const s = statusOf(m);
              return (
                <li
                  key={m.id}
                  className="flex items-center gap-3 rounded border border-brand-border bg-brand-surface px-3 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold">
                      {(m.tag ?? '').length > 0 ? `PRO ${m.tag}` : 'No PRO'}
                    </div>
                    <div className="truncate text-[11px] text-brand-muted">{m.fieldName}</div>
                    <div className="truncate text-[11px] text-brand-muted/70">
                      Driver {m.driverNumber ?? '—'} • {fmtDate(m.capturedAt)}
                    </div>
                    {m.lastError && (
                      <div className="truncate text-[11px] text-brand-error">{m.lastError}</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${s.tone}`}>
                      {s.text}
                    </span>
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => resend(m)}>
                      RE-SEND
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </PullToRefresh>
    </AppShell>
  );
}

function labelOf(m: MediaQueueItem): string {
  const pro = (m.tag ?? '').length > 0 ? m.tag : 'no PRO';
  return `${isSignatureMedia(m) ? 'POD/Signature' : 'Photo'} (${pro})`;
}
